import { faGlobe } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import moment from 'moment'
import { useContext, useState } from 'react'
import * as SETTINGS from '../../../constants/settings'
import { SiteConfigContext } from '../../../context/siteConfigContext'
import { toastError, toastSuccess } from '../../../utils/toast'
import Tooltip from '../../Utilities/Tooltip'
import ClearChatModal from './ClearChatModal'

const formatToUTCTime = (localTime) => {
  if (!localTime) return '00:00'
  return moment(localTime, 'HH:mm').utc().format('HH:mm')
}

const ClearChatSetting = ({ clearChatPolicy }) => {
  const { updateSetting } = useContext(SiteConfigContext)
  const [frequency, setFrequency] = useState(
    clearChatPolicy.schedule?.frequency || 'daily'
  )

  const [clearChatModal, setClearChatModal] = useState(false)

  const handleSubmit = async (e) => {
    e.preventDefault()
    const formData = new FormData(e.target)
    const updatedPolicy = JSON.parse(JSON.stringify(clearChatPolicy))

    const rawValues = Object.fromEntries(formData.entries())

    if (clearChatPolicy.mode === 'scheduled') {
      const localDay = rawValues['schedule.dayOfWeek'] || 'sunday'
      const localTime = rawValues['schedule.time']

      const utcMoment = moment(localTime, 'HH:mm').day(localDay).utc()

      updatedPolicy.schedule = {
        frequency: rawValues['schedule.frequency'],
        dayOfWeek: utcMoment.format('dddd').toLowerCase(),
        time: utcMoment.format('HH:mm')
      }
    }

    formData.forEach((value, name) => {
      if (name.startsWith('schedule')) return

      const paths = name.split('.')

      if (name === 'schedule.time') {
        value = formatToUTCTime(value)
      }

      let current = updatedPolicy
      for (let i = 0; i < paths.length; i++) {
        const part = paths[i]
        if (i === paths.length - 1) {
          current[part] = value
        } else {
          current[part] = current[part] || {}
          current = current[part]
        }
      }
    })

    const response = await updateSetting(
      SETTINGS.CLEAR_CHAT_POLICY,
      updatedPolicy
    )

    if (response && (response.status === 200 || response.status === 201)) {
      toastSuccess('Clear chat setting updated.')
    } else {
      toastError('Failed to update clear chat setting.')
    }
  }

  const scheduleSelector = (
    <>
      <select
        className="input p-2"
        name="schedule.frequency"
        defaultValue={frequency}
        onChange={(e) => setFrequency(e.target.value)}
      >
        <option value="daily">Daily</option>
        <option value="weekly">Weekly</option>
      </select>
      {frequency === 'weekly' && (
        <select
          className="input p-2"
          name="schedule.dayOfWeek"
          defaultValue={moment
            .utc(clearChatPolicy.schedule.time, 'HH:mm')
            .day(clearChatPolicy.schedule.dayOfWeek)
            .local()
            .format('dddd')
            .toLocaleLowerCase()}
        >
          <option value="sunday">Sunday</option>
          <option value="monday">Monday</option>
          <option value="tuesday">Tuesday</option>
          <option value="wednesday">Wednesday</option>
          <option value="thursday">Thursday</option>
          <option value="friday">Friday</option>
          <option value="saturday">Saturday</option>
        </select>
      )}
      <input
        className="input"
        name="schedule.time"
        defaultValue={moment
          .utc(clearChatPolicy.schedule.time, 'HH:mm')
          .local()
          .format('HH:mm')}
        type="time"
      />
      <p
        className="flex items-center px-2 py-1 border border-neutral-400 dark:border-neutral-600 rounded-sm text-sm select-none"
        alt="Timezone"
      >
        <FontAwesomeIcon icon={faGlobe} className="mr-1.5" />
        {Intl.DateTimeFormat().resolvedOptions().timeZone}
      </p>
    </>
  )

  const rollingSelector = (
    <>
      <input
        className="input max-w-24"
        type="number"
        name="rolling.value"
        min="1"
        step="1"
        defaultValue={clearChatPolicy.rolling?.value || 1}
      />
      <select
        className="input"
        name="rolling.unit"
        defaultValue={clearChatPolicy.rolling?.unit || 'hours'}
      >
        <option value="hours">Hours</option>
        <option value="days">Days</option>
      </select>
    </>
  )

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <h2>Clear Chat Setting</h2>
      </div>
      {clearChatPolicy.mode === 'scheduled' && (
        <div className="flex items-center gap-2">
          <h3>Scheduled</h3>
          <Tooltip>
            Public chat messages will be removed according to the selected
            schedule. Moderators will still see all messages in Chat History.
          </Tooltip>
        </div>
      )}
      {clearChatPolicy.mode === 'rolling' && (
        <div className="flex items-center gap-2">
          <h3>Rolling</h3>
          <Tooltip>
            Public chat messages will be removed once they are older than the
            selected rolling window. Moderators will still see all messages in
            Chat History.
          </Tooltip>
        </div>
      )}
      {clearChatPolicy.mode === 'manual' && (
        <button
          className={`w-fit button-cancel text-xs ${clearChatPolicy.mode === 'manual' ? '' : 'disabled'}`}
          type="button"
          onClick={() => setClearChatModal(true)}
          title="Clear chat history"
          onMouseEnter={(e) => e.target.focus()}
          onMouseLeave={(e) => e.target.blur()}
        >
          Clear Chat
        </button>
      )}
      {clearChatPolicy.mode !== 'manual' && (
        <form className="flex items-center gap-2" onSubmit={handleSubmit}>
          {clearChatPolicy.mode === 'scheduled' && scheduleSelector}
          {clearChatPolicy.mode === 'rolling' && rollingSelector}
          <button type="submit" className="button-confirm w-fit">
            Save
          </button>
        </form>
      )}
      <ClearChatModal
        open={clearChatModal}
        setOpen={setClearChatModal}
        clearChatPolicy={clearChatPolicy}
      />
    </div>
  )
}

export default ClearChatSetting
